package it.unibo.tuprolog.solve.libs.io.primitives

import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.core.TermFormatter
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.libs.io.primitives.IOPrimitiveUtils.ensuringArgumentIsOutputChannel
import it.unibo.tuprolog.solve.libs.io.primitives.IOPrimitiveUtils.writeTermAndReply
import it.unibo.tuprolog.solve.primitive.BinaryRelation
import it.unibo.tuprolog.solve.primitive.Solve

/**
 * Implements ISO's `write_canonical/2`: like [WriteCanonical1], but writing to the output stream identified by the
 * first argument (an alias or `$stream(...)` term) instead of the current output.
 *
 * @throws it.unibo.tuprolog.solve.exception.error.InstantiationError if the first argument is unbound.
 * @throws it.unibo.tuprolog.solve.exception.error.ExistenceError if it does not identify an open channel.
 * @throws it.unibo.tuprolog.solve.exception.error.DomainError (`stream_type`) if it identifies an input channel instead.
 *
 * Fails, rather than erroring, if the channel is closed.
 */
object WriteCanonical2 : BinaryRelation.NonBacktrackable<ExecutionContext>("write_canonical") {
    override val help: String =
        """
        `write_canonical(+Stream, @Term)`
        
        Writes `Term` to the output stream `Stream` in canonical form: atoms and functors are quoted where needed, operators are written in functional notation, and `'${'$'}VAR'(N)` terms are written as-is. It succeeds deterministically. `Stream` must be an alias (e.g. `user_output`) or a `${'$'}stream(out, Id)` term denoting an open stream: an unbound `Stream` raises an instantiation error, an unknown one an existence error, and an input stream a domain error (`stream_type`), where ISO prescribes a permission error. It fails, instead of raising an error, if the underlying channel has been closed.

        **Examples**

        ```prolog
        ?- write_canonical(user_output, f('A', 1 + 2)).
        % prints: f('A', '+'(1, 2))
        yes.

        ?- write_canonical(user_input, a).
        throws error(domain_error(stream_type, user_input), _).
        ```
        """.trimIndent()

    override fun Solve.Request<ExecutionContext>.computeOne(
        first: Term,
        second: Term,
    ): Solve.Response {
        val channel = ensuringArgumentIsOutputChannel(0)
        return writeTermAndReply(channel, second, TermFormatter.canonical())
    }
}
